import React from 'react';
import { motion } from 'framer-motion';
import { Award, Heart, Users, TrendingUp } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      {/* Hero Section */}
      <section className="relative py-20 bg-[#2A0000] text-white">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Our Story</h1>
            <p className="text-lg text-white/90 leading-relaxed">
              From Ahmedabad's rich textile heritage to Hubli's premier ethnic wear destination - a journey of passion, quality, and timeless elegance since 2014.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Heritage Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#2A0000] mb-6">
                Rooted in Tradition, Crafted with Excellence
              </h2>
              <p className="text-[#2C2C2C] leading-relaxed mb-4">
                Founded in 2014, Ahmedabad NX brings the timeless artistry of Gujarat's Ahmedabad textile heritage to Hubli, Karnataka. Our name reflects our origins - drawing inspiration from Ahmedabad's centuries-old weaving traditions and textile excellence.
              </p>
              <p className="text-[#2C2C2C] leading-relaxed mb-4">
                What began as a small boutique with a vision to make premium ethnic wear accessible has grown into a trusted destination for brides, families, and fashion enthusiasts across India.
              </p>
              <p className="text-[#2C2C2C] leading-relaxed">
                Today, we curate collections spanning authentic Banaras sarees, Mysore silks, designer gowns, elegant chudidhar sets, and contemporary cord sets - each piece handpicked for its quality, craftsmanship, and timeless appeal.
              </p>
            </div>
            <div>
              <img
                src="https://res.cloudinary.com/dhobeyvjt/image/upload/v1779696902/unnamed_4_zixcwo.webp"
                alt="Ahmedabad NX Showroom"
                className="w-full rounded-sm shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Milestones Timeline */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#2A0000] text-center mb-12">
            Our Journey
          </h2>

          <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex items-start space-x-6">
              <div className="w-24 h-24 bg-[#E5A93C] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="font-heading text-2xl font-bold text-white">2014</span>
              </div>
              <div className="flex-1 pt-4">
                <h3 className="font-heading text-xl font-bold text-[#2A0000] mb-2">The Beginning</h3>
                <p className="text-[#2C2C2C] leading-relaxed">
                  Ahmedabad NX opens its doors at Gokul Road, Hubli, with a curated collection of premium sarees and ethnic wear.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-6">
              <div className="w-24 h-24 bg-[#E5A93C] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="font-heading text-2xl font-bold text-white">2017</span>
              </div>
              <div className="flex-1 pt-4">
                <h3 className="font-heading text-xl font-bold text-[#2A0000] mb-2">500+ Collections</h3>
                <p className="text-[#2C2C2C] leading-relaxed">
                  Expanded our offerings to include designer gowns, chudidhar sets, and bridal collections, reaching 500+ unique designs.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-6">
              <div className="w-24 h-24 bg-[#E5A93C] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="font-heading text-2xl font-bold text-white">2020</span>
              </div>
              <div className="flex-1 pt-4">
                <h3 className="font-heading text-xl font-bold text-[#2A0000] mb-2">Online Presence</h3>
                <p className="text-[#2C2C2C] leading-relaxed">
                  Launched WhatsApp ordering and digital catalogue, making our collections accessible to customers across India and NRIs globally.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-6">
              <div className="w-24 h-24 bg-[#E5A93C] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="font-heading text-2xl font-bold text-white">2024</span>
              </div>
              <div className="flex-1 pt-4">
                <h3 className="font-heading text-xl font-bold text-[#2A0000] mb-2">1000+ Happy Brides</h3>
                <p className="text-[#2C2C2C] leading-relaxed">
                  Celebrated milestone of serving over 1000 brides and becoming Hubli's most trusted bridal wear specialist.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-[#FFFDF9]">
        <div className="container mx-auto px-4 md:px-8">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#2A0000] text-center mb-12">
            What We Stand For
          </h2>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#E5A93C] rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold text-[#2A0000] mb-2">Quality First</h3>
              <p className="text-sm text-[#2C2C2C] leading-relaxed">
                Every piece is handpicked for authentic fabrics, superior craftsmanship, and lasting beauty.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#E5A93C] rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold text-[#2A0000] mb-2">Customer Delight</h3>
              <p className="text-sm text-[#2C2C2C] leading-relaxed">
                Your satisfaction is our success. We go the extra mile to make your shopping experience memorable.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#E5A93C] rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold text-[#2A0000] mb-2">Trust & Transparency</h3>
              <p className="text-sm text-[#2C2C2C] leading-relaxed">
                Honest pricing, genuine fabrics, and transparent communication build lasting relationships.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#E5A93C] rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold text-[#2A0000] mb-2">Timeless Style</h3>
              <p className="text-sm text-[#2C2C2C] leading-relaxed">
                We blend tradition with contemporary trends to create looks that never go out of fashion.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#2A0000] mb-6">
              Visit Our Showroom
            </h2>
            <p className="text-[#2C2C2C] leading-relaxed mb-8">
              Experience our collections in person at our Gokul Road, Hubli location. Our team is ready to help you find the perfect ethnic wear for any occasion.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="https://wa.me/919538769669" target="_blank" rel="noopener noreferrer" className="btn-primary">
                Book an Appointment
              </a>
              <a href="tel:+919538769669" className="btn-outline">
                Call Us Now
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
